import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import type { MultiValue } from 'react-select';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { GetEstimateByMonthDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import momencik from '../../../common/momencik';

import { Option, Selector } from './Selector';

export interface EstimateByMonthSelectorProps {
  projectId: string;
  isMulti?: boolean;
  clearable?: boolean;
  readOnly?: boolean;
  value?: string | string[];
  className?: string;
  onChange?: (option: Option | MultiValue<Option> | null | undefined) => void;
}

export default (props: EstimateByMonthSelectorProps) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [estimateByMonths, setEstimateByMonths] = useState<
    GetEstimateByMonthDto[] | undefined
  >();

  useEffect(() => {
    let mounted = true;

    if (props.projectId) {
      pok.estimatesByMonth
        .findByProjectId(props.projectId)
        .then(data => {
          if (mounted) {
            setEstimateByMonths(data);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    } else {
      setEstimateByMonths([]);
    }

    return () => {
      mounted = false;
    };
  }, [pok.estimatesByMonth, notifications, props]);

  useEffect(() => {
    if (estimateByMonths && props.onChange) {
      if (props.isMulti) {
        const newEstimateItems = estimateByMonths?.filter(({ id }) =>
          props.value?.includes(id),
        );
        if ((props.value?.length || 0) !== (newEstimateItems?.length || 0)) {
          props.onChange(
            newEstimateItems.map(
              v => ({ value: v.id, label: v.profile?.name }) as Option,
            ),
          );
        }
      } else if (
        props.value &&
        !estimateByMonths.find(team => team.id === props.value)
      ) {
        props.onChange(null);
      }
    }
  }, [estimateByMonths, props]);

  const provider = async (text: string) => {
    const values =
      estimateByMonths?.filter(v =>
        v.profile.name?.toLocaleLowerCase().includes(text.toLocaleLowerCase()),
      ) || [];
    return values.map(v => ({
      label: `${v.profile?.name || ''}, ${momencik(v.date, 'YYYY-MM')}`,
      value: v.id,
    }));
  };

  if (!estimateByMonths) {
    return <Skeleton />;
  }

  return (
    <Selector
      uniqueKey={'estimateByMonth-' + JSON.stringify(estimateByMonths)}
      className={props.className}
      isMulti={props.isMulti}
      readOnly={props.readOnly || !estimateByMonths || !props.projectId}
      creatable={false}
      clearable={props.clearable}
      provider={provider}
      value={props.value}
      onChange={props.onChange}
    />
  );
};
